import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { File, Image, FileText, Download, Trash2, ExternalLink } from 'lucide-react';
import { fileApi } from '../../services/api';
import { Button, toast, ConfirmDialog } from '../../components/common';
import './AttachmentList.css';

export default function AttachmentList({ entityType, entityId, canDelete = true }) {
    const queryClient = useQueryClient();
    const [deleteFile, setDeleteFile] = React.useState(null);

    const { data: files, isLoading } = useQuery({
        queryKey: ['attachments', entityType, entityId],
        queryFn: () => fileApi.getAttachments(entityType, entityId),
        select: (res) => res.data?.data || [],
        enabled: !!entityType && !!entityId
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => fileApi.delete(id),
        onSuccess: () => {
            toast.success('File deleted');
            queryClient.invalidateQueries(['attachments', entityType, entityId]);
            setDeleteFile(null);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to delete');
        }
    });

    const handleDownload = async (id, filename) => {
        try {
            const res = await fileApi.download(id);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            toast.error('Failed to download file');
        }
    };

    const getFileIcon = (mimeType) => {
        if (mimeType?.startsWith('image/')) return Image;
        if (mimeType?.includes('pdf')) return FileText;
        return File;
    };

    const formatSize = (bytes) => {
        if (!bytes) return '0 B';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    };

    if (isLoading) {
        return <div className="attachments-loading">Loading attachments...</div>;
    }

    if (!files || files.length === 0) {
        return <div className="attachments-empty">No attachments</div>;
    }

    return (
        <div className="attachment-list">
            {files.map((file) => {
                const FileIcon = getFileIcon(file.mimeType);
                return (
                    <div key={file.id} className="attachment-item">
                        <div className="attachment-icon">
                            <FileIcon size={20} />
                        </div>
                        <div className="attachment-info">
                            <span className="attachment-name">{file.originalName}</span>
                            <span className="attachment-meta">{formatSize(file.size)}</span>
                        </div>
                        <div className="attachment-actions">
                            <button
                                className="attachment-btn"
                                onClick={() => handleDownload(file.id, file.originalName)}
                                title="Download"
                            >
                                <Download size={16} />
                            </button>
                            {canDelete && (
                                <button
                                    className="attachment-btn delete"
                                    onClick={() => setDeleteFile(file)}
                                    title="Delete"
                                >
                                    <Trash2 size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}

            <ConfirmDialog
                isOpen={!!deleteFile}
                onClose={() => setDeleteFile(null)}
                onConfirm={() => deleteMutation.mutate(deleteFile?.id)}
                title="Delete File"
                message={`Are you sure you want to delete "${deleteFile?.originalName}"?`}
                confirmText="Delete"
                variant="danger"
                loading={deleteMutation.isPending}
            />
        </div>
    );
}

AttachmentList.propTypes = {
    entityType: PropTypes.string.isRequired,
    entityId: PropTypes.string.isRequired,
    canDelete: PropTypes.bool
};
